import { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import MonetaryPopup from "./MonetaryPopup";
import dayjs from "dayjs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const BarChart = ({ monetaryTrend, patientReferred }: any) => {
  const containerRef = useRef<any>(null);
  const months = monetaryTrend.map((item: any) =>
    dayjs(item.month).format("MMM'YY")
  );
  const plansPurchased = monetaryTrend.map((item: any) =>
    item?.planPurchased ? Number(item?.planPurchased?.replace(/,/g, "")) : 0
  );
  const earnings = monetaryTrend.map((item: any) =>
    item?.earnings ? Number(item.earnings?.replace(/,/g, "")) : 0
  );
  const [selectedMonetary, setSelectedMonetary] = useState<any>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth;
    }
  }, []);

  const monetaryPopupHandler = () => {
    setSelectedMonetary(null);
  };

  const data = {
    labels: months,
    datasets: [
      {
        label: "Value of plan purchased",
        data: plansPurchased,
        backgroundColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            // This case happens on initial chart load
            return null;
          }
          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, "rgba(45, 0, 91, 1)");
          gradient.addColorStop(1, "rgba(67, 0, 135, 0.3)");

          return gradient;
        },
        borderWidth: 1,
        borderRadius: 6,
      },
      {
        label: "Rewards Earned",
        data: earnings,
        backgroundColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return null;
          }
          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, "rgba(168, 172, 255, 1)");
          gradient.addColorStop(1, "rgba(168, 172, 255, 0.3)");

          return gradient;
        },
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        anchor: "end",
        align: "end",
        formatter: (value: number) => `${value || ""}`,
        color: "#000",
        font: {
          weight: "bold",
        },
      },
    },
    scales: {
      y: {
        grid: {
          display: true,
          color: "rgba(211, 211, 211, 0.5)",
          drawBorder: false,
          drawTicks: false,
        },
        ticks: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        barThickness: 20,
      },
    },
    layout: {
      padding: {
        top: 50,
      },
    },
    onClick: (event: any, elements: any[]) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        setSelectedMonetary(index);
      }
    },
  };

  return (
    <div className="max-w-lg mx-auto">
      <div className="font-semibold text-xl text-left mb-4">Monetary Trend</div>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <span className="w-[15px] h-[16px] bg-[#2D005B] rounded" />
          <span className="text-xs text-left font-normal ml-6">
            Value of plan purchased
          </span>
        </div>
        <div className="flex items-center">
          <span className="w-[15px] h-[16px] bg-[#A8ACFF] rounded" />
          <span className="text-xs font-normal ml-6 text-left">
            Rewards Earned
          </span>
        </div>
      </div>
      <div
        ref={containerRef}
        style={{
          whiteSpace: "nowrap",
          overflowX: "scroll",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <div
          style={{
            width: `calc(${110 * months.length}px)`,
            minHeight: "250px",
          }}
        >
          <Bar data={data} options={options} />
        </div>
      </div>
      {selectedMonetary !== null ? (
        <MonetaryPopup
          monetaryTrend={monetaryTrend}
          selectedMonetary={selectedMonetary}
          patientReferred={patientReferred}
          monetaryPopupHandler={monetaryPopupHandler}
        />
      ) : null}
    </div>
  );
};

export default BarChart;
